import { resolveTimeZone } from './zoned-time';

const EMPTY = '—';

export interface FormatTimestampOptions {
  /** Include the year (default true). The dashboard feed drops it. */
  year?: boolean;
  /** Include seconds (default false). The audit log shows them. */
  seconds?: boolean;
}

/**
 * Locale-aware timestamp rendered in an EXPLICIT IANA timezone, with the
 * short zone name ("EDT") appended so the reader always knows which clock
 * they are looking at.
 *
 * Callers pass the relevant location's timezone; an unknown or invalid
 * timezone falls back to the browser's — never silently to UTC. `null`/empty
 * renders an em dash; an unparsable string is returned as-is.
 */
export function formatTimestamp(
  iso: string | null | undefined,
  locale: string,
  timeZone: string | null | undefined,
  options: FormatTimestampOptions = {},
): string {
  if (!iso) return EMPTY;
  const { year = true, seconds = false } = options;
  try {
    return new Intl.DateTimeFormat(locale, {
      ...(year ? { year: 'numeric' } : {}),
      month: 'short',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      ...(seconds ? { second: '2-digit' } : {}),
      timeZone: resolveTimeZone(timeZone),
      timeZoneName: 'short',
    }).format(new Date(iso));
  } catch {
    return iso;
  }
}
